import React, { useState, useRef, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { ArrowDownIcon } from '$partials/icon';
import { Listbox } from '@headlessui/react';

const Select = forwardRef((
    /**@type {Omit<Props, 'as' | 'look' | 'size'> & ExtendedProps} */
    {
        label,
        onChange = () => {},
        options,
        value = '',
        className,
        innerLabel,
        ...remainingProps
    },
    /**@type {React.ForwardedRef<(HTMLInputElement)>} */
    ref
)  => {
    const [ selectedOption, setSelectedOption ] = useState(value);
    const step = 5;
    const scrollRef = useRef();
    const isScrollRef = useRef();
    const selectRef = useRef();

    const setMove = (state) => isScrollRef.current = state;

    const move = () => {
        if (isScrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollTop + step;
            requestAnimationFrame(move);
        }
    };

    const handleChange = (value) => {
        onChange(value);
        setSelectedOption(value);
    };

    return (
        <div
            className={`relative width-100 black height-100 ${className ? className : ''}`}
            ref={selectRef}
            tabIndex={1}>

            {/* Hidden input */}
            <input
                ref={ref}
                type='hidden'
                {...remainingProps}
                value={selectedOption} />

            <Listbox
                onChange={handleChange}
                value={selectedOption}>
                {({ open }) => {
                    return (
                        <div className='relative'>
                            <Listbox.Button
                                className={'form-field-reset link-reset button inline-flex width-100 height-100 pl5 md-pl6 sm-pr5 pt6 pb6 md-pt5 md-pb5 '}>
                                <span className='flex flex-col width-100'>
                                    <p className='type-style-5 align-left py2 select-label'>{label}</p>
                                    <div className='flex justify-between items-center select-value'>
                                        <h3 className='type-style-3'>
                                            {options.find(({ value }) => value === selectedOption)?.label}
                                        </h3>
                                        <span
                                            aria-hidden={true}
                                            className='font-0 line-height-0 p3 md-p5'>
                                            <ArrowDownIcon/>
                                        </span>
                                    </div>
                                </span>
                            </Listbox.Button>

                            {open && (
                                <div
                                    className={'select-option-container absolute width-100 bg-white z6 top-0 border-bottom border-left border-right filter-dropshadow'}
                                    style={className === 'modal-search--select' ? { marginLeft: '-1px', width: 'calc(100% + 2px)' } : null }>
                                    {innerLabel && (
                                        <div className='inner-label'>
                                            {innerLabel}
                                        </div>
                                    )}
                                    <Listbox.Options
                                        className={`options overflow-auto ${options.length > 8 ? 'pb9' : ''}`}
                                        ref={scrollRef}>
                                        {options.filter(option => !option?.options?.hide )?.map((option, idx) => {
                                            return (
                                                <Listbox.Option
                                                    as={React.Fragment}
                                                    key={idx}
                                                    value={option.value}>
                                                    <li className={`cursor-pointer type-style-5 ${selectedOption === option.value ? 'bg-yellow' : ''}`}>
                                                        {option.label}
                                                    </li>
                                                </Listbox.Option>
                                            );
                                        })}

                                        {options.length > 8 && open && (
                                            <button
                                                className='form-field-reset black absolute flex py5 pl6 bottom-0 width-100 bg-white'
                                                onMouseLeave={() => setMove(false)}
                                                onMouseOver={() => { setMove(true); move();}}
                                                type='button'>
                                                <ArrowDownIcon/>
                                            </button>
                                        )}
                                    </Listbox.Options>
                                </div>
                            )}
                        </div>
                    );}}
            </Listbox>
        </div>
    );
});

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const propTypes = {
    innerLabel: PropTypes.string,
    onChange: PropTypes.func,
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    className: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })).isRequired
};

Select.propTypes = propTypes;
Select.displayName = 'Select';

export default Select;
